/**
 * Financial Translation Module - Dealer Showroom ROI.
 *
 * Staff channels: Sales Performance, Productivity, Retention.
 * Customer channels: Dwell Time → Conversion, Satisfaction, Repeat Business, CSI Manufacturer Bonuses.
 *
 * Evidence-backed coefficients adapted for automotive retail context.
 * Financial defaults updated to NADA Q1 2025 actuals.
 */

// ── Per-pathway effect sizes (retail-adapted from published research) ──
export const DS_PATHWAY_COEFFICIENTS = {
  daylight:       { channel: 'sales_performance', effect: 0.04, study: 'Heschong 1999', desc: '+4% sales in daylit retail spaces' },
  noise:          { channel: 'sales_performance', effect: 0.03, study: 'Banbury & Berry 2005', desc: '-3% negotiation effectiveness from noise' },
  air_quality:    { channel: 'sales_performance', effect: 0.05, study: 'Allen et al. 2015 COGfx', desc: '+5% decision quality with better air' },
  nature:         { channel: 'retention',         effect: 0.06, study: 'Bjornstad 2016', desc: '-6% absenteeism from nature access' },
  enrichment:     { channel: 'sales_performance', effect: 0.06, study: 'Nieuwenhuis 2014', desc: '+6% productivity in enriched environments' },
  movement:       { channel: 'productivity',      effect: 0.02, study: 'Conn 2009 meta-analysis', desc: '+2% productivity from workplace activity' },
  wayfinding:     { channel: 'productivity',      effect: 0.02, study: 'Jamshidi 2025', desc: '+2% FTE recovery from better customer flow' },
  visual_stress:  { channel: 'sales_performance', effect: 0.02, study: 'Wilkins 1989', desc: '-2% cognitive load from visual comfort' },
};

// ── CSI pathway weights (how much each pathway contributes to CSI score lift) ──
export const DS_CSI_PATHWAY_WEIGHTS = {
  noise:          { csiLift: 1.5, desc: 'Noise reduction most impactful on customer comfort surveys' },
  air_quality:    { csiLift: 1.0, desc: 'Air quality affects perceived dealership cleanliness/quality' },
  visual_stress:  { csiLift: 0.8, desc: 'Visual comfort contributes to overall facility impression' },
  enrichment:     { csiLift: 1.2, desc: 'Enriched environments drive positive experience ratings' },
  daylight:       { csiLift: 0.7, desc: 'Daylight quality improves overall facility satisfaction' },
  nature:         { csiLift: 0.5, desc: 'Nature elements contribute to relaxation during wait times' },
  wayfinding:     { csiLift: 0.8, desc: 'Easy navigation reduces frustration in customer journey' },
  movement:       { csiLift: 0.3, desc: 'Layout encouraging movement improves browsing experience' },
};

// ── CSI Tier Definitions ──
export const DS_CSI_TIERS = {
  tier1: { minScore: 85, quarterlyBonus: 200000, label: 'Tier 1', color: '#10B981' },
  tier2: { minScore: 75, quarterlyBonus: 100000, label: 'Tier 2', color: '#F59E0B' },
  tier3: { minScore: 0,  quarterlyBonus: 0,      label: 'Tier 3', color: '#EF4444' },
};

// ── F&I Revenue Per Unit (NADA Q1 2025) ──
export const DS_FI_PER_UNIT = {
  newVehicle: 1458,  // NADA Q1 2025: $1,458/unit F&I income
  usedVehicle: 749,  // NADA estimate for used vehicle F&I
};

// ── Staff role defaults (automotive dealership) ──
export const DS_ROLE_DEFAULTS = {
  SalesConsultant: {
    label: 'Sales Consultant',
    avgHourlyCost: 26,   // ~$55k/yr base
    replacementCostMultiple: 1.0,
    annualTurnoverRate: 0.35,
    pctTimeRework: 0.04,
    pctTimeKeyWork: 0.65,
  },
  ServiceAdvisor: {
    label: 'Service Advisor',
    avgHourlyCost: 23,   // ~$48k/yr
    replacementCostMultiple: 0.8,
    annualTurnoverRate: 0.28,
    pctTimeRework: 0.05,
    pctTimeKeyWork: 0.60,
  },
  FIManager: {
    label: 'F&I Manager',
    avgHourlyCost: 41,   // ~$85k/yr
    replacementCostMultiple: 1.5,
    annualTurnoverRate: 0.22,
    pctTimeRework: 0.03,
    pctTimeKeyWork: 0.70,
  },
  PartsStaff: {
    label: 'Parts Staff',
    avgHourlyCost: 18,   // ~$38k/yr
    replacementCostMultiple: 0.5,
    annualTurnoverRate: 0.30,
    pctTimeRework: 0.06,
    pctTimeKeyWork: 0.55,
  },
};

// ── Customer tier defaults (NADA Q1 2025 actuals) ──
export const DS_CUSTOMER_TIERS = {
  NewVehicle: {
    label: 'New Vehicle',
    avgTransaction: 46000,   // NADA 2025: avg new vehicle transaction $46K
    annualCustomers: 600,
    baseCloseRate: 0.25,
    baseReturnRate: 0.40,
    baseSatisfactionScore: 0.82,
    fiPerUnit: 1458,          // NADA Q1 2025 F&I per new unit
  },
  UsedVehicle: {
    label: 'Used Vehicle',
    avgTransaction: 22000,   // NADA 2025: avg used vehicle $22K (was $28K)
    annualCustomers: 400,
    baseCloseRate: 0.30,
    baseReturnRate: 0.35,
    baseSatisfactionScore: 0.78,
    fiPerUnit: 749,           // NADA estimate F&I per used unit
  },
  ServiceRO: {
    label: 'Service',
    avgTransaction: 800,     // Gross $218 but customer-facing transaction higher
    annualCustomers: 8000,
    baseCloseRate: 0.85,
    baseReturnRate: 0.55,
    baseSatisfactionScore: 0.75,
    fiPerUnit: 0,
  },
  PartsRetail: {
    label: 'Parts',
    avgTransaction: 200,
    annualCustomers: 3000,
    baseCloseRate: 0.90,
    baseReturnRate: 0.30,
    baseSatisfactionScore: 0.72,
    fiPerUnit: 0,
  },
};

// ── Staff Evidence Citations ──
export const DS_STAFF_EVIDENCE = {
  sales_performance: [
    { author: 'Heschong Mahone Group', year: 1999, finding: 'Retail stores with skylights showed 40% higher sales; conservative 4% per-pathway estimate', channel: 'Sales Performance' },
    { author: 'Allen et al.', year: 2015, finding: 'COGfx study: +8% cognitive function in green buildings; applied to decision accuracy', channel: 'Sales Performance' },
    { author: 'Nieuwenhuis et al.', year: 2014, finding: 'Enriched environments improve productivity by 6%', channel: 'Sales Performance' },
  ],
  productivity: [
    { author: 'Conn et al.', year: 2009, finding: 'Workplace activity programs improve work ability by ~2%', channel: 'Productivity' },
    { author: 'Jamshidi et al.', year: 2025, finding: 'Better wayfinding recovers ~2% FTE time in large facilities', channel: 'Productivity' },
  ],
  retention: [
    { author: 'Bjornstad et al.', year: 2016, finding: 'Nature/greenery access reduces absenteeism by 6%', channel: 'Retention' },
    { author: 'NADA', year: 2025, finding: 'Average dealership turnover 35% for sales staff; replacement cost ~$10-15k', channel: 'Retention' },
  ],
};

// ── Customer Evidence Citations ──
export const DS_CUSTOMER_EVIDENCE = {
  conversion: [
    { author: 'Heschong Mahone Group', year: 1999, finding: 'Daylit retail environments increase transaction likelihood', channel: 'Conversion' },
    { author: 'Spence et al.', year: 2014, finding: 'Multisensory retail environments increase dwell time and purchase intent', channel: 'Conversion' },
    { author: 'NADA', year: 2025, finding: 'Front-end gross only $108/unit (Q1 2025); F&I at $1,458/unit now primary profit driver', channel: 'Conversion' },
  ],
  satisfaction: [
    { author: 'JD Power', year: 2024, finding: 'Facility quality is top-5 driver of CSI scores in dealership satisfaction surveys', channel: 'Satisfaction' },
    { author: 'Ford Motor Company', year: 2025, finding: 'Signature 2.0 pilot stores in Mexico showed +30% sales lift', channel: 'Satisfaction' },
  ],
  repeat_business: [
    { author: 'Cox Automotive', year: 2023, finding: 'Customers citing facility comfort are 1.4x more likely to return for service', channel: 'Repeat Business' },
  ],
  csi_bonus: [
    { author: 'JD Power / Kimoby', year: 2024, finding: 'CSI Tier-1 (>=85): up to $800K+/yr in manufacturer bonuses; below 75 = penalties + lost allocation', channel: 'CSI Bonuses' },
    { author: 'NADA', year: 2025, finding: '16,972 franchised dealers; manufacturer incentive programs tied to CSI performance', channel: 'CSI Bonuses' },
    { author: 'Optimum Info', year: 2025, finding: 'Service absorption at 68.1% (Q1 2025); CSI drives service retention', channel: 'CSI Bonuses' },
  ],
};

// ── Channel calculation helpers ──

function salesPerformanceChannel(pathwayScores, totalAnnualPayroll) {
  const daylight = Math.max(0, pathwayScores.daylight || 0);
  const air = Math.max(0, 1.0 + (pathwayScores.air_quality || 0));
  const enrichment = Math.max(0, pathwayScores.enrichment || 0);
  const noiseImprovement = 1.0 - Math.abs(pathwayScores.noise || 0);
  const visualImprovement = 1.0 - Math.abs(pathwayScores.visual_stress || 0);

  const saving =
    (daylight * DS_PATHWAY_COEFFICIENTS.daylight.effect +
     air * DS_PATHWAY_COEFFICIENTS.air_quality.effect +
     enrichment * DS_PATHWAY_COEFFICIENTS.enrichment.effect +
     noiseImprovement * DS_PATHWAY_COEFFICIENTS.noise.effect +
     visualImprovement * DS_PATHWAY_COEFFICIENTS.visual_stress.effect) *
    totalAnnualPayroll;

  const pct = totalAnnualPayroll > 0 ? saving / totalAnnualPayroll : 0;
  return { value: saving, pct };
}

function productivityChannel(pathwayScores, totalKeyWorkCost) {
  const movement = Math.max(0, pathwayScores.movement || 0);
  const wayfinding = Math.max(0, pathwayScores.wayfinding || 0);

  const saving =
    (movement * DS_PATHWAY_COEFFICIENTS.movement.effect +
     wayfinding * DS_PATHWAY_COEFFICIENTS.wayfinding.effect) *
    totalKeyWorkCost;

  const pct = totalKeyWorkCost > 0 ? saving / totalKeyWorkCost : 0;
  return { value: saving, pct };
}

function retentionChannel(pathwayScores, annualTurnoverCost) {
  const nature = Math.max(0, pathwayScores.nature || 0);
  const saving = nature * DS_PATHWAY_COEFFICIENTS.nature.effect * annualTurnoverCost;
  const pct = annualTurnoverCost > 0 ? saving / annualTurnoverCost : 0;
  return { value: saving, pct };
}

/**
 * Calculate staff financial ROI for dealer showroom.
 */
export function calculateDealerStaffRoi(brainHealthScore, pathwayScores, peopleData, projectCost = 0) {
  const people = Array.isArray(peopleData) ? peopleData : [peopleData];

  let totalAnnualPayroll = 0;
  let totalHeadcount = 0;
  let totalReplacementPool = 0;
  let totalKeyWorkCost = 0;
  let weightedTurnoverRate = 0;

  for (const person of people) {
    const hc = Number(person.headcount) || 0;
    const hourly = Number(person.avgHourlyCost || person.avg_hourly_cost) || 0;
    const hoursPerWeek = Number(person.hoursPerWeek || person.hours_per_week) || 40;
    const annualHours = hoursPerWeek * 47;
    const replMult = Number(person.replacementCostMultiple || person.avg_replacement_cost_pct) || 1.0;
    const keyPct = Number(person.pctTimeKeyWork || person.pct_time_key_work) || 0.60;
    const turnover = Number(person.annualTurnoverRate || person.annual_turnover_rate) || 0.30;

    const annual = hc * hourly * annualHours;
    totalAnnualPayroll += annual;
    totalHeadcount += hc;
    totalReplacementPool += hc * hourly * annualHours * replMult;
    totalKeyWorkCost += annual * keyPct;
    weightedTurnoverRate += turnover * hc;
  }

  const emptyResult = {
    overallBrainHealthScore: brainHealthScore,
    channels: [
      { name: 'Sales Performance', annualSavings: 0, pct: 0 },
      { name: 'Productivity', annualSavings: 0, pct: 0 },
      { name: 'Retention', annualSavings: 0, pct: 0 },
    ],
    totalAnnualImpact: 0,
    totalAnnualBenefit: 0,
    totalAnnualSavings: 0,
    paybackPct: 0,
    paybackYears: null,
    roi: null,
  };

  if (totalHeadcount === 0) return emptyResult;

  const avgTurnover = weightedTurnoverRate / totalHeadcount;
  const avgReplacement = totalReplacementPool / totalHeadcount;
  const annualTurnoverCost = totalHeadcount * avgTurnover * avgReplacement;

  const sales = salesPerformanceChannel(pathwayScores, totalAnnualPayroll);
  const productivity = productivityChannel(pathwayScores, totalKeyWorkCost);
  const retention = retentionChannel(pathwayScores, annualTurnoverCost);

  const totalAnnual = sales.value + productivity.value + retention.value;
  const paybackPct = projectCost > 0 ? (totalAnnual / projectCost * 100) : 0;
  const paybackYears = totalAnnual > 0 ? (projectCost / totalAnnual) : null;
  const roi = projectCost > 0 ? totalAnnual / projectCost : null;

  return {
    overallBrainHealthScore: Math.round(brainHealthScore * 10000) / 10000,
    channels: [
      { name: 'Sales Performance', annualSavings: Math.round(sales.value * 100) / 100, pct: Math.round(sales.pct * 10000) / 10000 },
      { name: 'Productivity', annualSavings: Math.round(productivity.value * 100) / 100, pct: Math.round(productivity.pct * 10000) / 10000 },
      { name: 'Retention', annualSavings: Math.round(retention.value * 100) / 100, pct: Math.round(retention.pct * 10000) / 10000 },
    ],
    totalAnnualImpact: Math.round(totalAnnual * 100) / 100,
    totalAnnualBenefit: Math.round(totalAnnual * 100) / 100,
    totalAnnualSavings: Math.round(totalAnnual * 100) / 100,
    paybackPct: Math.round(paybackPct * 100) / 100,
    paybackYears: paybackYears !== null ? Math.round(paybackYears * 100) / 100 : null,
    roi: roi !== null ? Math.round(roi * 10000) / 10000 : null,
  };
}

/**
 * Calculate CSI score lift from environmental pathway improvements.
 * Each pathway contributes 0.3-1.5 CSI points based on its quality score.
 */
export function calculateCsiLift(pathwayScores) {
  let totalLift = 0;
  const breakdown = [];

  for (const [pathway, weights] of Object.entries(DS_CSI_PATHWAY_WEIGHTS)) {
    const raw = pathwayScores[pathway] || 0;
    // For negative pathways (noise, air_quality, visual_stress), invert to get improvement
    const isNeg = ['noise', 'air_quality', 'visual_stress'].includes(pathway);
    const quality = isNeg ? Math.max(0, 1 + raw) : Math.max(0, raw);
    const lift = quality * weights.csiLift;
    totalLift += lift;
    breakdown.push({
      pathway,
      quality: Math.round(quality * 100) / 100,
      csiLift: Math.round(lift * 100) / 100,
      maxLift: weights.csiLift,
      desc: weights.desc,
    });
  }

  return {
    totalLift: Math.round(totalLift * 100) / 100,
    breakdown,
  };
}

/**
 * Calculate CSI tier and manufacturer bonus based on base score + environmental lift.
 */
export function calculateCsiBonus(csiBaseScore, pathwayScores) {
  const { totalLift, breakdown } = calculateCsiLift(pathwayScores);
  const projectedScore = Math.min(100, csiBaseScore + totalLift);

  // Determine current tier (before improvements)
  let currentTier, currentBonus;
  if (csiBaseScore >= 85) {
    currentTier = DS_CSI_TIERS.tier1;
    currentBonus = currentTier.quarterlyBonus * 4;
  } else if (csiBaseScore >= 75) {
    currentTier = DS_CSI_TIERS.tier2;
    currentBonus = currentTier.quarterlyBonus * 4;
  } else {
    currentTier = DS_CSI_TIERS.tier3;
    currentBonus = 0;
  }

  // Determine projected tier (after improvements)
  let projectedTier, projectedBonus;
  if (projectedScore >= 85) {
    projectedTier = DS_CSI_TIERS.tier1;
    projectedBonus = projectedTier.quarterlyBonus * 4;
  } else if (projectedScore >= 75) {
    projectedTier = DS_CSI_TIERS.tier2;
    projectedBonus = projectedTier.quarterlyBonus * 4;
  } else {
    projectedTier = DS_CSI_TIERS.tier3;
    projectedBonus = 0;
  }

  const annualBonusDelta = projectedBonus - currentBonus;

  return {
    csiBaseScore,
    csiLift: totalLift,
    projectedScore: Math.round(projectedScore * 10) / 10,
    currentTier: currentTier.label,
    currentTierColor: currentTier.color,
    currentAnnualBonus: currentBonus,
    projectedTier: projectedTier.label,
    projectedTierColor: projectedTier.color,
    projectedAnnualBonus: projectedBonus,
    annualBonusDelta,
    tierCrossing: currentTier.label !== projectedTier.label,
    breakdown,
  };
}

/**
 * Calculate customer financial ROI for dealer showroom.
 *
 * Environmental quality → increased dwell time → higher close rate,
 * better satisfaction scores → more repeat/referral business.
 * Now includes F&I revenue on additional closes and CSI manufacturer bonuses.
 */
export function calculateDealerCustomerRoi(brainHealthScore, pathwayScores, customerData, projectCost = 0) {
  const tiers = Array.isArray(customerData) ? customerData : [customerData];

  const daylight = Math.max(0, pathwayScores.daylight || 0);
  const enrichment = Math.max(0, pathwayScores.enrichment || 0);
  const wayfinding = Math.max(0, pathwayScores.wayfinding || 0);
  const noiseImprovement = 1.0 - Math.abs(pathwayScores.noise || 0);
  const airImprovement = Math.max(0, 1.0 + (pathwayScores.air_quality || 0));
  const visualImprovement = 1.0 - Math.abs(pathwayScores.visual_stress || 0);
  const nature = Math.max(0, pathwayScores.nature || 0);

  // Conversion lift: up to +3% close rate improvement
  const conversionLiftPct = (daylight * 0.01 + enrichment * 0.01 + wayfinding * 0.01);
  // Satisfaction lift: up to +2% CSI improvement
  const satisfactionLiftPct = (noiseImprovement * 0.008 + airImprovement * 0.006 + visualImprovement * 0.006);
  // Repeat business lift: up to +2% return rate improvement
  const repeatLiftPct = (nature * 0.01 + enrichment * 0.01);

  let totalConversion = 0;
  let totalFiRevenue = 0;
  let totalSatisfaction = 0;
  let totalRepeat = 0;

  // Find CSI base score from customer data (or use default)
  let csiBaseScore = 78;

  for (const tier of tiers) {
    const customers = Number(tier.annualCustomers) || 0;
    const avgTx = Number(tier.avgTransaction) || 0;
    const baseClose = Number(tier.baseCloseRate) || 0.30;
    const baseReturn = Number(tier.baseReturnRate) || 0.40;
    const fiPerUnit = Number(tier.fiPerUnit) || 0;

    if (tier.csiBaseScore != null) {
      csiBaseScore = Number(tier.csiBaseScore);
    }

    // Conversion: additional customers who close due to better environment
    const additionalCloses = customers * conversionLiftPct;
    totalConversion += additionalCloses * avgTx;

    // F&I revenue on additional closes (new profit driver per NADA 2025)
    totalFiRevenue += additionalCloses * fiPerUnit;

    // Satisfaction: revenue preserved through better experience (reduced defection)
    const satisfactionValue = customers * baseClose * avgTx * satisfactionLiftPct;
    totalSatisfaction += satisfactionValue;

    // Repeat: additional returning customers
    const additionalReturns = customers * baseClose * repeatLiftPct;
    totalRepeat += additionalReturns * avgTx * baseReturn;
  }

  // CSI Manufacturer Bonuses (4th customer channel)
  const csiResult = calculateCsiBonus(csiBaseScore, pathwayScores);
  const csiBonusDelta = csiResult.annualBonusDelta;

  const totalAnnual = totalConversion + totalFiRevenue + totalSatisfaction + totalRepeat + csiBonusDelta;
  const paybackPct = projectCost > 0 ? (totalAnnual / projectCost * 100) : 0;
  const paybackYears = totalAnnual > 0 ? (projectCost / totalAnnual) : null;
  const roi = projectCost > 0 ? totalAnnual / projectCost : null;

  return {
    overallBrainHealthScore: Math.round(brainHealthScore * 10000) / 10000,
    channels: [
      { name: 'Dwell Time → Conversion', annualSavings: Math.round(totalConversion * 100) / 100, pct: conversionLiftPct },
      { name: 'F&I Revenue Lift', annualSavings: Math.round(totalFiRevenue * 100) / 100, pct: totalConversion > 0 ? totalFiRevenue / totalConversion : 0 },
      { name: 'Customer Satisfaction', annualSavings: Math.round(totalSatisfaction * 100) / 100, pct: satisfactionLiftPct },
      { name: 'Repeat Business', annualSavings: Math.round(totalRepeat * 100) / 100, pct: repeatLiftPct },
      { name: 'CSI Manufacturer Bonuses', annualSavings: Math.round(csiBonusDelta * 100) / 100, pct: 0, csiDetail: csiResult },
    ],
    totalAnnualImpact: Math.round(totalAnnual * 100) / 100,
    totalAnnualBenefit: Math.round(totalAnnual * 100) / 100,
    totalAnnualSavings: Math.round(totalAnnual * 100) / 100,
    paybackPct: Math.round(paybackPct * 100) / 100,
    paybackYears: paybackYears !== null ? Math.round(paybackYears * 100) / 100 : null,
    roi: roi !== null ? Math.round(roi * 10000) / 10000 : null,
    csiDetail: csiResult,
  };
}
